import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService.js';
import { pushNotificationService } from '../services/pushNotificationService.js';

const AuthContext = createContext();

/**
 * Hook personalizado para usar o contexto de autenticação
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

/**
 * Provider de autenticação
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Verificar se há usuário logado ao carregar a aplicação
  useEffect(() => {
    const initAuth = () => {
      console.log('[AuthContext] Initializing auth...');
      const isAuth = authService.isAuthenticated();
      console.log('[AuthContext] isAuthenticated:', isAuth);
      
      if (isAuth) {
        const currentUser = authService.getCurrentUser();
        console.log('[AuthContext] Current user loaded:', currentUser ? 'yes' : 'no');
        setUser(currentUser);
      } else {
        console.warn('[AuthContext] User not authenticated on init');
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Inicializar notificações push quando o usuário estiver autenticado
  useEffect(() => {
    if (user) {
      console.log('[AuthContext] Usuário autenticado, inicializando notificações push...');
      pushNotificationService.initialize()
        .then(success => {
          if (success) {
            console.log('[AuthContext] Notificações push inicializadas com sucesso');
          } else {
            console.warn('[AuthContext] Não foi possível inicializar notificações push');
          }
        })
        .catch(error => {
          console.error('[AuthContext] Erro ao inicializar notificações push:', error);
        });
    }
  }, [user]);

  /**
   * Realiza login
   */
  const login = async (email, password) => {
    try {
      const result = await authService.login(email, password);
      setUser(result.user);
      return { success: true, emailVerified: result.user.emailVerified !== false };
    } catch (error) {
      return {
        success: false,
        message: error.message,
      };
    }
  };

  /**
   * Atualiza o estado do usuário a partir do localStorage (sem chamar API)
   */
  const refreshUserFromStorage = () => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  };

  /**
   * Realiza logout
   */
  const logout = async () => {
    // Limpa as notificações push antes de fazer logout
    try {
      await pushNotificationService.cleanup();
    } catch (error) {
      console.error('[AuthContext] Erro ao limpar notificações no logout:', error);
    }
    
    authService.logout();
    setUser(null);
  };

  /**
   * Atualiza dados do usuário
   */
  const updateUser = async (userData) => {
    try {
      const result = await authService.updateUser(userData);
      setUser(result.user);
      return { success: true, user: result.user };
    } catch (error) {
      return {
        success: false,
        message: error.message,
      };
    }
  };

  /**
   * Recarrega dados do usuário do servidor
   */
  const refreshUser = async () => {
    try {
      const result = await authService.refreshUserData();
      if (result.success) {
        setUser(result.user);
      }
      return result;
    } catch (error) {
      return {
        success: false,
        message: error.message,
      };
    }
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    refreshUser,
    refreshUserFromStorage,
    isAuthenticated: !!user,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
